import React, { useCallback, useMemo } from 'react'

import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { faGripDotsVertical, faPen, faTrash } from '@fortawesome/pro-regular-svg-icons'
import { faEye, faEyeSlash } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { HStack, T, Tooltip } from 'govwell-ui'
import { runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import styled from 'styled-components'
import { ViewColumnTemplateFragment } from 'types/graphql'

import { IconOnlyButton } from 'src/components/shared/StyledComponents'
import DeleteCustomColumnModal from 'src/components/TableViews/ManageColumnsDrawer/DeleteCustomColumnModal'
import UpdateCustomColumnModal from 'src/components/TableViews/ManageColumnsDrawer/UpdateCustomColumnModal'
import useDisclosure from 'src/hooks/use-disclosure'

const VisibilityButton = styled(IconOnlyButton)<{ $isVisible: boolean }>`
  border: none;
  ${({ $isVisible, theme }) => !$isVisible && `color: ${theme.colorIcon}`};
`

// Separated from the outer sortable wrapper to avoid expensive re-renders during DnD.
// useSortable subscribes to DndContext, which updates on every pointer move -- so any
// component calling useSortable re-renders every frame. With hundreds of columns in the
// drawer, that's thousands of re-renders per drag. By isolating the heavy content (hooks,
// modals, tooltips) into this observer-wrapped component with only stable props, we let
// the outer wrapper re-render cheaply (just a Flex + grip icon) while this stays memoized.
// IMPORTANT: do NOT pass useSortable's attributes/listeners here -- they're new objects
// every render and would bust the memo.
type ItemContentProps = {
  columnTemplate: ViewColumnTemplateFragment
  isEditable: boolean
  isLoading: boolean
  isVisible: boolean
  onColumnVisibilityToggled: (generatedId: string) => Promise<unknown>
}
const ItemContent = observer(
  ({
    columnTemplate,
    isEditable,
    isLoading,
    isVisible,
    onColumnVisibilityToggled,
  }: ItemContentProps) => {
    const deleteColumnModalState = useDisclosure()
    const updateColumnModalState = useDisclosure()

    const handleColumnTypeToggled = useCallback(async () => {
      await runInAction(async () => {
        await onColumnVisibilityToggled(columnTemplate.generatedId)
      })
    }, [columnTemplate.generatedId, onColumnVisibilityToggled])

    return (
      <HStack wrap="nowrap" gap={1} width="auto">
        {isEditable && (
          <>
            <Tooltip content="Delete custom column">
              <IconOnlyButton
                icon={<FontAwesomeIcon icon={faTrash} />}
                onClick={deleteColumnModalState.open}
                size="small"
              />
            </Tooltip>
            {deleteColumnModalState.isOpen && (
              <DeleteCustomColumnModal
                columnTemplate={columnTemplate}
                modalState={deleteColumnModalState}
              />
            )}
            <Tooltip content="Edit custom column">
              <IconOnlyButton
                icon={<FontAwesomeIcon icon={faPen} />}
                onClick={updateColumnModalState.open}
                size="small"
              />
            </Tooltip>
            {updateColumnModalState.isOpen && (
              <UpdateCustomColumnModal
                columnTemplate={columnTemplate}
                modalState={updateColumnModalState}
                mode="edit"
                onColumnVisibilityToggled={onColumnVisibilityToggled}
              />
            )}
          </>
        )}
        <Tooltip content={isVisible ? 'Hide column' : 'Show column'} side="left">
          <VisibilityButton
            icon={<FontAwesomeIcon icon={isVisible ? faEye : faEyeSlash} />}
            $isVisible={isVisible}
            onClick={handleColumnTypeToggled}
            disabled={isLoading}
            size="small"
            className={isLoading ? 'opacity-40' : undefined}
          />
        </Tooltip>
      </HStack>
    )
  }
)

// Thin sortable wrapper -- re-renders every frame during drag, but the render is trivially
// cheap (one Flex, one div, one icon). The expensive ItemContent above is memoized and
// skips re-renders entirely while dragging.
type Props = {
  columnTemplate: ViewColumnTemplateFragment
  displayLabel?: string
  isEditable: boolean
  isLoading: boolean
  isVisible: boolean
  onColumnVisibilityToggled: (generatedId: string) => Promise<unknown>
}
const ManageColumnsDrawerItem = ({
  columnTemplate,
  displayLabel,
  isEditable,
  isLoading,
  isVisible,
  onColumnVisibilityToggled,
}: Props) => {
  const { attributes, isDragging, listeners, setNodeRef, transform, transition } = useSortable({
    id: columnTemplate.generatedId,
  })

  const wrapperStyle = useMemo<React.CSSProperties>(
    () => ({
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0 : 1,
    }),
    [transform, transition, isDragging]
  )

  return (
    <HStack justify="space-between" gap={3} width="100%" ref={setNodeRef} style={wrapperStyle}>
      <HStack gap={1} width="auto">
        <div {...attributes} {...listeners} className="cursor-grab p-2">
          <FontAwesomeIcon icon={faGripDotsVertical} />
        </div>
        <T.Body>
          {displayLabel ?? columnTemplate.labelSentenceCase}
          {displayLabel && (
            <span className="ml-1 text-xs text-neutral-400">
              ({columnTemplate.labelSentenceCase})
            </span>
          )}
        </T.Body>
      </HStack>
      <ItemContent
        columnTemplate={columnTemplate}
        isEditable={isEditable}
        isLoading={isLoading}
        isVisible={isVisible}
        onColumnVisibilityToggled={onColumnVisibilityToggled}
      />
    </HStack>
  )
}

export default React.memo(ManageColumnsDrawerItem)
